import React from 'react';
import styles from './_finance-addons-editor.module.scss';
import {IRuleViolation} from "../../../../Interfaces/Application/IApplicationQuote";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {ComboBox, ComboBoxChangeEvent} from "@progress/kendo-react-dropdowns";
import {
  CarletonEnumName, carletonInsuranceCoverageTypes, CarletonItem,
  FinanceLoanClass,
  FinanceQuote, getEnumForValue,
  getFullName, getOptionsFromEnumAndLoanClass,
  hasValue,
  toDollars
} from "@amsanalytics/react-shared-library";
import QuoterError from "../../QuoterError/QuoterError";
import {getFinanceQuoteErrorMessages} from "../../../../utils/quoteUtils";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import {CardHeader, CardTitle} from "@progress/kendo-react-layout";
import {Input} from "@progress/kendo-react-inputs";
import {getFeeAmountForCarletonInsuranceCoverageType} from "../../../../utils/utils";
import {IAppCustomer} from "../../../../Interfaces/Application/IAppCustomer";

export interface FinanceAddonsEditorProps {
  selectedFinanceQuote: FinanceQuote;
  selectedFinanceLoanClass: FinanceLoanClass | undefined;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  applicationFinanceErrors: IRuleViolation[];
  lookups: ILookupItems;
  appCustomers: IAppCustomer[];
  onUpdateCustomerAddon: (customerId: string, fieldName: string, value: any) => void;
  onUpdateQuoteAddon: (fieldName: string, value: any) => void;
}

export default function FinanceAddonsEditor (props:  FinanceAddonsEditorProps) {
  const errorMessages = getFinanceQuoteErrorMessages(props.applicationFinanceErrors);
  const customerDetails = props.selectedFinanceQuote.financeQuoteCustomerDetails;
  
  const getOptionsForEnum = (carletonEnumName: CarletonEnumName) : CarletonItem[] => {
    return getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, carletonEnumName, props.selectedFinanceQuote.financeLoanClassId);
  }
  
  const addCoverageTypes = getOptionsForEnum("ADDCoverageCode",);
  const ahCoverageTypes = getOptionsForEnum("AHCoverageCode");
  const motorClubCoverageTypes = getOptionsForEnum("MotorClubCoverageCode");
  const iuiCoverageTypes = getOptionsForEnum("IUICoverageCode");
  const lifeCoverageTypes = getOptionsForEnum("LifeCoverageCode");
  const propertyCoverageTypes = getOptionsForEnum("PropertyCode");
  const tpdCoverageTypes = getOptionsForEnum("TPDCoverageCode");
  const selectedPropertyCoverageCode = getEnumForValue(propertyCoverageTypes, props.selectedFinanceQuote.propertyCode);
  const selectedMotorClubCoverageCode = getEnumForValue(motorClubCoverageTypes, props.selectedFinanceQuote.motorClubCoverageCode);
  
  // These addons are specific for each customer
  const handleUpdateCustomerLevelInsuranceAddon = (
    customerId: string,
    fieldName: string, 
    value: any
  ): void => {
    props.onUpdateCustomerAddon(customerId, fieldName, value);
  };

  // These addons are applied at the quote level, not customer level
  const handleUpdateQuoteLevelInsuranceAddon = (
    fieldName: string,
    value: any
  ): void => {
    props.onUpdateQuoteAddon(fieldName, value);
  };
  
  const propertyFeeAmount = getFeeAmountForCarletonInsuranceCoverageType(props.selectedFinanceLoanClass, props.selectedFinanceQuote, carletonInsuranceCoverageTypes.propertyInsurance);
  const motorClubFeeAmount = getFeeAmountForCarletonInsuranceCoverageType(props.selectedFinanceLoanClass, props.selectedFinanceQuote, carletonInsuranceCoverageTypes.motorClub);

  const hasAnyAddons = propertyCoverageTypes.length > 0
    || motorClubCoverageTypes.length > 0
    || addCoverageTypes.length > 0
    || ahCoverageTypes.length > 0
    || iuiCoverageTypes.length > 0
    || lifeCoverageTypes.length > 0
    || tpdCoverageTypes.length > 0;

  if (!hasAnyAddons) {
    return (
      <div className={styles.financeAddonsEditorContainer} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
        There are no addons to configure for this loan class. Contact support if addons are needed.
      </div>
    );
  }

  return (
    <div className={styles.financeAddonsEditorContainer}>
      <Card className={styles.addonsCard}>
        <div className={styles.addonsInputs}>
          {propertyCoverageTypes.length > 0 &&
            <div className={styles.sectionItem}>
              <ComboBox
                disabled={props.financeQuoteFieldIsDisabled("addons")}
                valid={!hasValue(errorMessages.addons)}
                label={'PPI (Property Insurance)'}
                data={propertyCoverageTypes}
                textField="option"
                suggest={true}
                clearButton={false}
                value={selectedPropertyCoverageCode}
                onChange={(event: ComboBoxChangeEvent) => handleUpdateQuoteLevelInsuranceAddon("propertyCode", event.value.value)}
              />
              <Input
                readOnly={true}
                label={'Property Premium'}
                value={toDollars(propertyFeeAmount)}
                type='text'
              />
              <QuoterError message={errorMessages.addons}/>
            </div>
          }
          {motorClubCoverageTypes.length > 0 &&
            <div className={styles.sectionItem}>
              <ComboBox
                disabled={props.financeQuoteFieldIsDisabled("addons")}
                valid={!hasValue(errorMessages.addons)}
                label={'Motor Club'}
                data={motorClubCoverageTypes}
                textField="option"
                suggest={true}
                clearButton={false}
                value={selectedMotorClubCoverageCode}
                onChange={(event: ComboBoxChangeEvent) => handleUpdateQuoteLevelInsuranceAddon('motorClubCoverageCode', event.value.value)}
              />
              <Input
                readOnly={true}
                label={'Motor Club Premium'}
                value={toDollars(motorClubFeeAmount)}
                type='text'
              />
              <QuoterError message={errorMessages.addons}/>
            </div>
          }
        </div>
        
        <div className={styles.customerDetailColumns}>
          {customerDetails.map((customerDetail, key) => {
            const customer = props.appCustomers.find(ac => ac.customerId === customerDetail.customerId)?.customer;
            const customerName = getFullName(customer);
            const selectedAddCoverageCode = getEnumForValue(addCoverageTypes, customerDetail.addCoverageCode);
            const selectedAhCoverageCode = getEnumForValue(ahCoverageTypes, customerDetail.ahCoverageCode);
            const selectedIuiCoverageCode = getEnumForValue(iuiCoverageTypes, customerDetail.iuiCoverageCode);
            const selectedLifeCoverageCode = getEnumForValue(lifeCoverageTypes, customerDetail.lifeCoverageCode);
            const selectedTpdCoverageCode = getEnumForValue(tpdCoverageTypes, customerDetail.tpdCoverageCode);
            
            return (
              <Card className={styles.customerDetailColumn} key={key}>
                <CardHeader>
                  <CardTitle className={styles.customerNameHeader}>
                    {customerName}
                  </CardTitle>
                </CardHeader>
                <CardContent className={styles.addonsInputs}>
                  <div>
                    {addCoverageTypes.length > 0 &&
                      <div className={styles.sectionItem}>
                        <ComboBox
                          disabled={props.financeQuoteFieldIsDisabled("addons")}
                          valid={!hasValue(errorMessages.contractType)}
                          label={'ADD (Accidental Death and Dismemberment)'}
                          data={addCoverageTypes}
                          textField="option"
                          suggest={true}
                          clearButton={false}
                          value={selectedAddCoverageCode}
                          onChange={(event: ComboBoxChangeEvent) => handleUpdateCustomerLevelInsuranceAddon(customerDetail.customerId, 'addCoverageCode', event.value.value)}
                        />
                        <QuoterError message={errorMessages.addons}/>
                      </div>
                    }
                    {ahCoverageTypes.length > 0 &&
                      <div className={styles.sectionItem}>
                        <ComboBox
                          disabled={props.financeQuoteFieldIsDisabled("addons")}
                          valid={!hasValue(errorMessages.addons)}
                          label={'AH (Accident and Health)'}
                          data={ahCoverageTypes}
                          textField="option"
                          suggest={true}
                          clearButton={false}
                          value={selectedAhCoverageCode}
                          onChange={(event: ComboBoxChangeEvent) => handleUpdateCustomerLevelInsuranceAddon(customerDetail.customerId, 'ahCoverageCode', event.value.value)}
                        />
                        <QuoterError message={errorMessages.addons}/>
                      </div>
                    }
                    {iuiCoverageTypes.length > 0 &&
                      <div className={styles.sectionItem}>
                        <ComboBox
                          disabled={props.financeQuoteFieldIsDisabled("addons")}
                          valid={!hasValue(errorMessages.addons)}
                          label={'IUI (Involuntary Unemployment Insurance)'}
                          data={iuiCoverageTypes}
                          textField="option"
                          suggest={true}
                          clearButton={false}
                          value={selectedIuiCoverageCode}
                          onChange={(event: ComboBoxChangeEvent) => handleUpdateCustomerLevelInsuranceAddon(customerDetail.customerId, 'iuiCoverageCode', event.value.value)}
                        />
                        <QuoterError message={errorMessages.addons}/>
                      </div>
                    }
                    {lifeCoverageTypes.length > 0 &&
                      <div className={styles.sectionItem}>
                        <ComboBox
                          disabled={props.financeQuoteFieldIsDisabled("addons")}
                          valid={!hasValue(errorMessages.addons)}
                          label={'CL (Credit Life)'}
                          data={lifeCoverageTypes}
                          textField="option"
                          suggest={true}
                          clearButton={false}
                          value={selectedLifeCoverageCode}
                          onChange={(event: ComboBoxChangeEvent) => handleUpdateCustomerLevelInsuranceAddon(customerDetail.customerId, 'lifeCoverageCode', event.value.value)}
                        />
                        <QuoterError message={errorMessages.addons}/>
                      </div>
                    }
                    {tpdCoverageTypes.length > 0 &&
                      <div className={styles.sectionItem}>
                        <ComboBox
                          disabled={props.financeQuoteFieldIsDisabled("addons")}
                          valid={!hasValue(errorMessages.addons)}
                          label={'TPD (Total and Permanent Disability)'}
                          data={tpdCoverageTypes}
                          textField="option"
                          suggest={true}
                          clearButton={false}
                          value={selectedTpdCoverageCode}
                          onChange={(event: ComboBoxChangeEvent) => handleUpdateCustomerLevelInsuranceAddon(customerDetail.customerId, 'tpdCoverageCode', event.value.value)}
                        />
                        <QuoterError message={errorMessages.addons}/>
                      </div>
                    }
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/*Results (Premium and Term)*/}
          <Card className={styles.customerDetailColumn} >
            <CardHeader>
              <CardTitle className={styles.customerNameHeader}>
                Terms and Premiums 
              </CardTitle>
            </CardHeader>
            <CardContent className={styles.addonsInputs}>
              <div>
                {addCoverageTypes.length > 0 &&
                  <div className={styles.sectionItem}>
                    {(props.selectedFinanceQuote.addTerm ?? 0) > 0 &&
                      <>
                        <Input
                          readOnly={true}
                          label={'ADD Term'}
                          value={props.selectedFinanceQuote.addTerm}
                          type='number'
                        />
                        <Input
                          readOnly={true}
                          label={'ADD Premium'}
                          value={toDollars(props.selectedFinanceQuote.addPremium)}
                          type='text'
                        />
                      </>
                    }
                    <QuoterError message={errorMessages.addons}/>
                  </div>
                }
                {ahCoverageTypes.length > 0 &&
                  <div className={styles.sectionItem}>
                    <Input
                      readOnly={true}
                      label={'AH Term'}
                      value={props.selectedFinanceQuote.ahTerm}
                      type='number'
                    />
                    <Input
                      readOnly={true}
                      label={'AH Premium'}
                      value={toDollars(props.selectedFinanceQuote.ahPremium)}
                      type='text'
                    />
                    <QuoterError message={errorMessages.addons}/>
                  </div>
                }
                {iuiCoverageTypes.length > 0 &&
                  <div className={styles.sectionItem}>
                    <Input
                      readOnly={true}
                      label={'IUI Term'}
                      value={props.selectedFinanceQuote.iuiTerm}
                      type='number'
                    />
                    <Input
                      readOnly={true}
                      label={'IUI Premium'}
                      value={toDollars(props.selectedFinanceQuote.iuiPremium)}
                      type='text'
                    />
                    <QuoterError message={errorMessages.addons}/>
                  </div>
                }
                {lifeCoverageTypes.length > 0 &&
                  <div className={styles.sectionItem}>
                    <Input
                      readOnly={true}
                      label={'CL Term'}
                      value={props.selectedFinanceQuote.dlTerm}
                      type='number'
                    />
                    <Input
                      readOnly={true}
                      label={'CL Premium'}
                      value={toDollars(props.selectedFinanceQuote.dlPremium)}
                      type='text'
                    />
                    <QuoterError message={errorMessages.addons}/>
                  </div>
                }
                {tpdCoverageTypes.length > 0 &&
                  <div className={styles.sectionItem}>
                    <Input
                      readOnly={true}
                      label={'TPD Term'}
                      value={props.selectedFinanceQuote.tpdTerm}
                      type='number'
                    />
                    <Input
                      readOnly={true}
                      label={'TPD Premium'}
                      value={toDollars(props.selectedFinanceQuote.tpdPremium)}
                      type='text'
                    />
                    <QuoterError message={errorMessages.addons}/>
                  </div>
                }
              </div>
            </CardContent>
          </Card>
        </div>
      </Card>
    </div>
  );
}
